import React, { useState } from 'react'
import {
  Trophy, Award, Star, Medal, Shield, Clock, BookOpen, Baby, Plus, Edit2, Gift,
  Users, TrendingUp, Settings2, Flame, ChevronUp, ChevronDown,
} from 'lucide-react'
import {
  useBadges, useCreateBadge, useUpdateBadge, useDeleteBadge,
  useLeaderboard, useEmployeeXpList, useAwardXp,
  useGamificationSettings, useUpdateGamificationSettings,
  type Badge, type BadgeFilters, type LeaderboardEntry, type EmployeeXpEntry,
} from '../hooks/gamification/useGamification'
import { useOrganizationsForSelect } from '../hooks/organizations/useOrganizations'

// ==================== Constants ====================

const CATEGORY_LABELS: Record<string, string> = {
  punctuality: 'Пунктуальность',
  attendance: 'Посещаемость',
  testing: 'Тестирование',
  milestone: 'Milestone',
  custom: 'Другое',
}

const CATEGORY_COLORS: Record<string, string> = {
  punctuality: 'bg-blue-100 text-blue-800',
  attendance: 'bg-green-100 text-green-800',
  testing: 'bg-purple-100 text-purple-800',
  milestone: 'bg-amber-100 text-amber-800',
  custom: 'bg-gray-100 text-gray-800',
}

const TRIGGER_LABELS: Record<string, string> = {
  on_time_checkin: 'Приход вовремя',
  attendance_streak: 'Стрик посещаемости',
  no_late_streak: 'Стрик без опозданий',
  test_passed: 'Сдал тест',
  test_perfect_score: '100% за тест',
  working_days_total: 'Всего рабочих дней',
  early_checkin: 'Ранний приход',
  manual: 'Ручная выдача',
}

const XP_SOURCE_LABELS: Record<string, string> = {
  checkin_on_time: 'Приход вовремя',
  checkout_on_time: 'Уход вовремя',
  no_late_day: 'День без опозданий',
  attendance_streak_bonus: 'Бонус за стрик',
  test_passed: 'Тест сдан',
  test_perfect_score: 'Тест на 100%',
  badge_earned: 'Значок получен',
  manual_award: 'Ручное начисление',
  manual_deduct: 'Ручное списание',
}

const ICON_MAP: Record<string, React.ElementType> = {
  star: Star, trophy: Trophy, award: Award, medal: Medal, shield: Shield,
  clock: Clock, 'book-open': BookOpen, baby: Baby,
}

const RANK_STYLES = [
  'bg-yellow-100 text-yellow-800 font-bold',
  'bg-gray-100 text-gray-700 font-semibold',
  'bg-amber-50 text-amber-700 font-semibold',
]

// ==================== Page ====================

export function Gamification() {
  const [activeTab, setActiveTab] = useState<'badges' | 'leaderboard' | 'xp' | 'settings'>('badges')

  const tabs = [
    { id: 'badges' as const, label: 'Значки', icon: Award },
    { id: 'leaderboard' as const, label: 'Рейтинг', icon: TrendingUp },
    { id: 'xp' as const, label: 'XP Сотрудников', icon: Users },
    { id: 'settings' as const, label: 'Настройки', icon: Settings2 },
  ]

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Trophy className="h-8 w-8 text-amber-500" />
            Геймификация
          </h1>
          <p className="text-muted-foreground mt-1">XP, значки и рейтинг сотрудников</p>
        </div>
      </div>

      <div className="border-b">
        <nav className="flex space-x-4" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {activeTab === 'badges' && <BadgesTab />}
      {activeTab === 'leaderboard' && <LeaderboardTab />}
      {activeTab === 'xp' && <EmployeeXpTab />}
      {activeTab === 'settings' && <SettingsTab />}
    </div>
  )
}

// ==================== Badges Tab ====================

function BadgesTab() {
  const [filters, setFilters] = useState<BadgeFilters>({ page: 1, limit: 20 })
  const { data: badgesData, isLoading } = useBadges(filters)
  const [formOpen, setFormOpen] = useState(false)
  const [editBadge, setEditBadge] = useState<Badge | null>(null)
  const deleteBadge = useDeleteBadge()

  const badges = badgesData?.data || []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <input
          placeholder="Поиск значков..."
          className="border rounded-md px-3 py-2 text-sm w-64"
          value={filters.search || ''}
          onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
        />
        <button
          onClick={() => { setEditBadge(null); setFormOpen(true) }}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Создать значок
        </button>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 font-medium">Значок</th>
              <th className="text-left p-3 font-medium">Категория</th>
              <th className="text-left p-3 font-medium">Триггер</th>
              <th className="text-center p-3 font-medium">XP</th>
              <th className="text-center p-3 font-medium">Выдано</th>
              <th className="text-center p-3 font-medium">Статус</th>
              <th className="text-right p-3 font-medium">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Загрузка...</td></tr>
            ) : badges.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Нет значков</td></tr>
            ) : badges.map((badge: Badge) => {
              const BadgeIcon = ICON_MAP[badge.icon || ''] || Award
              return (
                <tr key={badge.id} className="hover:bg-muted/30">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: badge.color ? `${badge.color}20` : '#f3f4f6' }}>
                        <BadgeIcon className="h-4 w-4" style={{ color: badge.color || '#6b7280' }} />
                      </div>
                      <div>
                        <div className="font-medium">{badge.name}</div>
                        {badge.description && <div className="text-xs text-muted-foreground">{badge.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${CATEGORY_COLORS[badge.category] || CATEGORY_COLORS.custom}`}>
                      {CATEGORY_LABELS[badge.category] || badge.category}
                    </span>
                  </td>
                  <td className="p-3 text-muted-foreground">{TRIGGER_LABELS[badge.trigger_type] || badge.trigger_type}</td>
                  <td className="p-3 text-center font-medium">{badge.xp_reward}</td>
                  <td className="p-3 text-center">{badge.awarded_count ?? 0}</td>
                  <td className="p-3 text-center">
                    {badge.is_active
                      ? <span className="text-green-600 text-xs">Активен</span>
                      : <span className="text-gray-400 text-xs">Неактивен</span>}
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => { setEditBadge(badge); setFormOpen(true) }} className="p-1 hover:bg-muted rounded" title="Редактировать">
                      <Edit2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {badgesData && badgesData.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            disabled={filters.page === 1}
            onClick={() => setFilters({ ...filters, page: (filters.page || 1) - 1 })}
            className="px-3 py-1 border rounded text-sm disabled:opacity-50"
          >
            Назад
          </button>
          <span className="text-sm text-muted-foreground">
            {filters.page || 1} / {badgesData.totalPages}
          </span>
          <button
            disabled={(filters.page || 1) >= badgesData.totalPages}
            onClick={() => setFilters({ ...filters, page: (filters.page || 1) + 1 })}
            className="px-3 py-1 border rounded text-sm disabled:opacity-50"
          >
            Далее
          </button>
        </div>
      )}

      {formOpen && <BadgeFormDialog badge={editBadge} onClose={() => setFormOpen(false)} />}
    </div>
  )
}

// ==================== Badge Form Dialog ====================

function BadgeFormDialog({ badge, onClose }: { badge: Badge | null; onClose: () => void }) {
  const createBadge = useCreateBadge()
  const updateBadge = useUpdateBadge()
  const isEdit = !!badge

  const [form, setForm] = useState({
    name: badge?.name || '',
    description: badge?.description || '',
    icon: badge?.icon || 'star',
    color: badge?.color || '#f59e0b',
    category: badge?.category || 'custom',
    trigger_type: badge?.trigger_type || 'manual',
    trigger_config: badge?.trigger_config || {},
    xp_reward: badge?.xp_reward ?? 0,
    is_repeatable: badge?.is_repeatable ?? false,
    sort_order: badge?.sort_order ?? 0,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (isEdit) {
        await updateBadge.mutateAsync({ id: badge.id, data: form })
      } else {
        await createBadge.mutateAsync(form)
      }
      onClose()
    } catch (error) {
      console.error('Error saving badge:', error)
    }
  }

  const triggerNeedsStreakDays = ['no_late_streak', 'attendance_streak'].includes(form.trigger_type)
  const triggerNeedsTotalDays = form.trigger_type === 'working_days_total'
  const triggerNeedsCount = form.trigger_type === 'on_time_checkin'

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">{isEdit ? 'Редактировать значок' : 'Создать значок'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Название</label>
              <input className="w-full border rounded-md px-3 py-2 text-sm" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Описание</label>
              <input className="w-full border rounded-md px-3 py-2 text-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Иконка</label>
                <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}>
                  {Object.keys(ICON_MAP).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Цвет</label>
                <input type="color" className="w-full h-9 border rounded-md" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Категория</label>
                <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Триггер</label>
                <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.trigger_type} onChange={(e) => setForm({ ...form, trigger_type: e.target.value, trigger_config: {} })}>
                  {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            {triggerNeedsStreakDays && (
              <div>
                <label className="block text-sm font-medium mb-1">Кол-во дней стрика</label>
                <input type="number" min={1} className="w-full border rounded-md px-3 py-2 text-sm" value={form.trigger_config.streak_days ?? ''} onChange={(e) => setForm({ ...form, trigger_config: { streak_days: Number(e.target.value) } })} />
              </div>
            )}
            {triggerNeedsTotalDays && (
              <div>
                <label className="block text-sm font-medium mb-1">Всего рабочих дней</label>
                <input type="number" min={1} className="w-full border rounded-md px-3 py-2 text-sm" value={form.trigger_config.total_days ?? ''} onChange={(e) => setForm({ ...form, trigger_config: { total_days: Number(e.target.value) } })} />
              </div>
            )}
            {triggerNeedsCount && (
              <div>
                <label className="block text-sm font-medium mb-1">Кол-во раз</label>
                <input type="number" min={1} className="w-full border rounded-md px-3 py-2 text-sm" value={form.trigger_config.count ?? ''} onChange={(e) => setForm({ ...form, trigger_config: { count: Number(e.target.value) } })} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">XP награда</label>
                <input type="number" min={0} className="w-full border rounded-md px-3 py-2 text-sm" value={form.xp_reward} onChange={(e) => setForm({ ...form, xp_reward: Number(e.target.value) })} />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_repeatable} onChange={(e) => setForm({ ...form, is_repeatable: e.target.checked })} />
                  Повторяемый
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-md text-sm">Отмена</button>
              <button
                type="submit"
                disabled={createBadge.isPending || updateBadge.isPending}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
              >
                {createBadge.isPending || updateBadge.isPending ? 'Сохранение...' : isEdit ? 'Сохранить' : 'Создать'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// ==================== Leaderboard Tab ====================

function LeaderboardTab() {
  const [period, setPeriod] = useState('all_time')
  const [organizationId, setOrganizationId] = useState<string>('')
  const { data: organizations = [] } = useOrganizationsForSelect()
  const { data: entries = [], isLoading } = useLeaderboard({
    limit: 100,
    period,
    organization_id: organizationId || undefined,
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {(['all_time', 'month', 'week'] as const).map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              period === p ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {p === 'all_time' ? 'Все время' : p === 'month' ? 'Месяц' : 'Неделя'}
          </button>
        ))}
        <select
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
          className="border rounded-md px-3 py-1.5 text-sm bg-background"
        >
          <option value="">Все организации</option>
          {organizations.map((org: { id: string; name: string }) => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-center p-3 font-medium w-16">#</th>
              <th className="text-left p-3 font-medium">Сотрудник</th>
              <th className="text-left p-3 font-medium">Организация</th>
              <th className="text-left p-3 font-medium">Отдел</th>
              <th className="text-center p-3 font-medium">Уровень</th>
              <th className="text-center p-3 font-medium">XP</th>
              <th className="text-center p-3 font-medium">
                <Flame className="h-4 w-4 inline" /> Стрик
              </th>
              <th className="text-center p-3 font-medium">Значки</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Загрузка...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Нет данных</td></tr>
            ) : entries.map((entry: LeaderboardEntry) => (
              <tr key={entry.employee_id} className="hover:bg-muted/30">
                <td className="p-3 text-center">
                  <span className={`inline-flex w-7 h-7 items-center justify-center rounded-full text-xs ${
                    entry.rank <= 3 ? RANK_STYLES[entry.rank - 1] : ''
                  }`}>
                    {entry.rank}
                  </span>
                </td>
                <td className="p-3 font-medium">
                  {entry.employee?.last_name} {entry.employee?.first_name}
                </td>
                <td className="p-3 text-muted-foreground">{entry.employee?.department?.organization?.name || '—'}</td>
                <td className="p-3 text-muted-foreground">{entry.employee?.department?.name || '—'}</td>
                <td className="p-3 text-center">
                  <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs font-medium">
                    {entry.level}
                  </span>
                </td>
                <td className="p-3 text-center font-semibold">{period !== 'all_time' && entry.period_xp !== undefined ? entry.period_xp : entry.total_xp}</td>
                <td className="p-3 text-center">
                  {entry.current_streak_days > 0 ? (
                    <span className="inline-flex items-center gap-1 text-orange-600">
                      <Flame className="h-3 w-3" />{entry.current_streak_days}
                    </span>
                  ) : '—'}
                </td>
                <td className="p-3 text-center">{entry.badges_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ==================== Employee XP Tab ====================

function EmployeeXpTab() {
  const [search, setSearch] = useState('')
  const [organizationId, setOrganizationId] = useState<string>('')
  const [page, setPage] = useState(1)
  const { data: organizations = [] } = useOrganizationsForSelect()
  const { data: xpData, isLoading } = useEmployeeXpList({
    search,
    page,
    limit: 20,
    sort: 'total_xp:desc',
    organization_id: organizationId || undefined,
  })
  const [awardOpen, setAwardOpen] = useState(false)

  const entries = xpData?.data || []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            placeholder="Поиск по ФИО..."
            className="border rounded-md px-3 py-2 text-sm w-64"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          />
          <select
            value={organizationId}
            onChange={(e) => { setOrganizationId(e.target.value); setPage(1) }}
            className="border rounded-md px-3 py-2 text-sm bg-background"
          >
            <option value="">Все организации</option>
            {organizations.map((org: { id: string; name: string }) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>
        <button onClick={() => setAwardOpen(true)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:bg-primary/90">
          <Gift className="h-4 w-4" /> Начислить XP
        </button>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 font-medium">Сотрудник</th>
              <th className="text-left p-3 font-medium">Организация</th>
              <th className="text-left p-3 font-medium">Отдел</th>
              <th className="text-center p-3 font-medium">Уровень</th>
              <th className="text-center p-3 font-medium">XP</th>
              <th className="text-center p-3 font-medium">Стрик</th>
              <th className="text-center p-3 font-medium">Значки</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Загрузка...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Нет данных</td></tr>
            ) : entries.map((entry: EmployeeXpEntry) => (
              <tr key={entry.employee_id} className="hover:bg-muted/30">
                <td className="p-3 font-medium">{entry.employee.last_name} {entry.employee.first_name}</td>
                <td className="p-3 text-muted-foreground">{entry.employee.department?.organization?.name || '—'}</td>
                <td className="p-3 text-muted-foreground">{entry.employee.department?.name || '—'}</td>
                <td className="p-3 text-center">
                  <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs font-medium">{entry.level}</span>
                </td>
                <td className="p-3 text-center font-semibold">{entry.total_xp}</td>
                <td className="p-3 text-center">
                  {entry.current_streak_days > 0
                    ? <span className="text-orange-600 inline-flex items-center gap-1"><Flame className="h-3 w-3" />{entry.current_streak_days}</span>
                    : '—'}
                </td>
                <td className="p-3 text-center">{entry.badges_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {xpData && xpData.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Назад</button>
          <span className="text-sm text-muted-foreground">{page} / {xpData.totalPages}</span>
          <button disabled={page >= xpData.totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Далее</button>
        </div>
      )}

      {awardOpen && <AwardXpDialog onClose={() => setAwardOpen(false)} />}
    </div>
  )
}

// ==================== Award XP Dialog ====================

function AwardXpDialog({ onClose }: { onClose: () => void }) {
  const awardXp = useAwardXp()
  const [employeeId, setEmployeeId] = useState('')
  const [amount, setAmount] = useState(0)
  const [description, setDescription] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await awardXp.mutateAsync({ employee_id: employeeId, amount, description })
      onClose()
    } catch (error) {
      console.error('Error awarding XP:', error)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Начислить XP</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">ID Сотрудника</label>
              <input className="w-full border rounded-md px-3 py-2 text-sm" required placeholder="UUID сотрудника" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Количество XP</label>
              <input type="number" className="w-full border rounded-md px-3 py-2 text-sm" required value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
              <p className="text-xs text-muted-foreground mt-1">Положительное — начисление, отрицательное — списание</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Причина</label>
              <input className="w-full border rounded-md px-3 py-2 text-sm" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-md text-sm">Отмена</button>
              <button type="submit" disabled={awardXp.isPending} className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
                {awardXp.isPending ? 'Начисление...' : 'Начислить'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// ==================== Settings Tab ====================

function SettingsTab() {
  const { data: settings, isLoading } = useGamificationSettings()
  const updateSettings = useUpdateGamificationSettings()
  const [form, setForm] = useState<Record<string, any>>({})
  const [hasChanges, setHasChanges] = useState(false)

  React.useEffect(() => {
    if (settings) {
      setForm({
        is_enabled: settings.is_enabled,
        xp_on_time_checkin: settings.xp_on_time_checkin,
        xp_on_time_checkout: settings.xp_on_time_checkout,
        xp_no_late_day: settings.xp_no_late_day,
        xp_test_passed: settings.xp_test_passed,
        xp_test_perfect: settings.xp_test_perfect,
        xp_streak_multiplier: settings.xp_streak_multiplier,
        leaderboard_visible_to_all: settings.leaderboard_visible_to_all,
      })
      setHasChanges(false)
    }
  }, [settings])

  const handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync(form)
      setHasChanges(false)
    } catch (error) {
      console.error('Error saving settings:', error)
    }
  }

  if (isLoading) return <div className="p-8 text-center text-muted-foreground">Загрузка...</div>

  const xpFields = [
    { key: 'xp_on_time_checkin', label: 'XP за приход вовремя' },
    { key: 'xp_on_time_checkout', label: 'XP за уход вовремя' },
    { key: 'xp_no_late_day', label: 'Бонус за день без опозданий' },
    { key: 'xp_test_passed', label: 'XP за сдачу теста' },
    { key: 'xp_test_perfect', label: 'XP за 100% за тест' },
  ]

  return (
    <div className="max-w-2xl space-y-6">
      <div className="border rounded-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Геймификация</h3>
            <p className="text-sm text-muted-foreground">Включить/выключить модуль для всей организации</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" checked={form.is_enabled ?? true} onChange={(e) => handleChange('is_enabled', e.target.checked)} />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        <hr />

        <div>
          <h3 className="font-semibold mb-3">Значения XP</h3>
          <div className="grid grid-cols-2 gap-4">
            {xpFields.map(({ key, label }) => (
              <div key={key}>
                <label className="block text-sm font-medium mb-1">{label}</label>
                <input type="number" min={0} className="w-full border rounded-md px-3 py-2 text-sm" value={form[key] ?? 0} onChange={(e) => handleChange(key, Number(e.target.value))} />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium mb-1">Множитель стрик-бонуса</label>
              <input type="number" min={1} step={0.1} className="w-full border rounded-md px-3 py-2 text-sm" value={form.xp_streak_multiplier ?? 1.5} onChange={(e) => handleChange('xp_streak_multiplier', Number(e.target.value))} />
            </div>
          </div>
        </div>

        <hr />

        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Видимость рейтинга</h3>
            <p className="text-sm text-muted-foreground">Показывать рейтинг всем сотрудникам</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" checked={form.leaderboard_visible_to_all ?? true} onChange={(e) => handleChange('leaderboard_visible_to_all', e.target.checked)} />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>
      </div>

      {hasChanges && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={updateSettings.isPending}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {updateSettings.isPending ? 'Сохранение...' : 'Сохранить настройки'}
          </button>
        </div>
      )}
    </div>
  )
}
